/**
 * Geocoding feature — shared API client.
 *
 * Calls the backend's /api/geocoding endpoints, which proxy a provider
 * (currently Mapbox v6) and normalize the response shape. Use this from any
 * feature that needs city autocomplete: carbon accounting (admin), the
 * journey planner, friend management, etc.
 *
 * See docs/integrations/mapbox.md for context.
 */

import { authApi } from '../auth/api';
import type { LatLng } from '../../entities/geo/types';

export interface CityResult {
  /** Stable provider id */
  id: string;
  /** Short label, e.g. "Lisbon" */
  name: string;
  /** Long label, e.g. "Lisbon, Portugal" */
  displayName: string;
  country: string | null;
  region: string | null;
  /** What the result is. Countries only appear for callers that opt in via
   *  `includeCountries` (trip form); optional for backward compatibility. */
  kind?: 'city' | 'country';
  lat: number;
  lng: number;
}

/** Convenience helper: extract just the coordinates from a CityResult. */
export const cityToLatLng = (city: CityResult): LatLng => ({
  lat: city.lat,
  lng: city.lng,
});

/**
 * Free-text city search for autocomplete inputs.
 *
 * Returns an empty array for queries shorter than 2 characters
 * (the backend short-circuits without calling the provider).
 *
 * Pass `includeCountries` to also match whole countries ("Portugal") —
 * used by the trip form's location fields; pickers that need an actual
 * city (friends, calculator) leave it off.
 */
export const searchCities = async (
  query: string,
  opts?: { limit?: number; includeCountries?: boolean; signal?: AbortSignal }
): Promise<CityResult[]> => {
  const params: Record<string, string | number> = { q: query };
  if (opts?.limit) params.limit = opts.limit;
  if (opts?.includeCountries) params.kinds = 'city,country';
  // Anonymous access: without a JWT the auth-gated route 401s, so the
  // logged-out trip form searches via the public, IP-quota'd twin instead
  // (same response shape — see backend/routes/publicGeocoding.js).
  const path = localStorage.getItem('authToken')
    ? '/geocoding/cities/search'
    : '/public/geocoding/cities/search';
  const response = await authApi.get(path, {
    params,
    signal: opts?.signal,
  });
  return response.data?.results ?? [];
};

/** `searchCities` preset for the trip form: cities plus whole countries. */
export const searchPlacesWithCountries = (
  query: string,
  opts?: { limit?: number; signal?: AbortSignal }
): Promise<CityResult[]> => searchCities(query, { ...opts, includeCountries: true });
